/* Print A Bill — build fingerprint.
 *
 * Produces a canonical, deterministic representation of one build as it exists
 * in the cart: the parent line plus every accessory line sharing its _build_id.
 *
 * WHAT THIS IS FOR
 * A change detector. It answers "has anything about this build changed since we
 * cached it?" It is NOT authority. A matching fingerprint never authorises
 * skipping a /cart.js read — restoration always reconstructs from live cart
 * data. See pab-mockup-bridge.js, which reads the cart unconditionally.
 *
 * WHY NOT specSignature()
 * That function is a hand-maintained field list, and WP-5 proved it fails
 * silently: customDenomination was simply absent, so value-only edits never
 * registered as changes. It also works on a spec object, so it cannot see
 * accessory relationships. This module enumerates real cart properties and
 * removes a documented denylist, so a property introduced later is covered
 * without anyone remembering to add it.
 */
(function () {
  'use strict';

  /* Bumping this invalidates every cached record, which is correct whenever the
     representation itself changes. */
  var SCHEMA = 'pabfp:v1';

  /* Properties excluded from the fingerprint.
   *
   * The test for every entry: can this change while the customer's
   * configuration stays identical? If yes, including it causes false
   * invalidation. Nothing the customer controls is excluded. */
  var EXCLUDED = {
    // Theme-asset CDN URL. Moves on every theme deploy with no build change.
    '_preview_image': 'presentation — asset URL changes with theme version',
    // The five below are derived from configuration plus live variant prices.
    // A supplier reprice would otherwise read as a build change.
    '_total_cents': 'derived — configuration plus live prices',
    '_total_cents_exact': 'derived — pre-rounding form of _total_cents',
    '_full_build_cents': 'derived — parent plus accessories',
    '_accessory_cents': 'derived — accessory subtotal',
    '_allocation_drift_cents': 'derived — rounding residue of the per-unit allocation',
    // Human-readable duplicate of _accessory_qtys. Including both double-counts
    // the same fact and churns when only label formatting changes.
    '_accessories_list': 'presentation — duplicate of _accessory_qtys'
  };

  /* Cart-line keys are deliberately not part of the fingerprint. Shopify
     rotates them whenever line properties are updated, so including one would
     make every edit look like a different build — the opposite of what a change
     detector is for. Identity is carried by _build_id, which is included. */

  function isExcluded(key) {
    return Object.prototype.hasOwnProperty.call(EXCLUDED, key);
  }

  /* Consistent value normalisation so that 2, '2' and ' 2 ' cannot produce
     three different fingerprints for the same build. */
  function normalizeValue(value) {
    if (value === null || value === undefined) return '';
    if (typeof value === 'boolean') return value ? 'true' : 'false';
    if (typeof value === 'number') return isFinite(value) ? String(value) : '';
    return String(value).trim();
  }

  function serializeProperties(properties) {
    var source = properties || {};
    return Object.keys(source)
      .filter(function (key) { return !isExcluded(key); })
      .sort()
      .map(function (key) {
        return encodeURIComponent(key) + '=' + encodeURIComponent(normalizeValue(source[key]));
      })
      .join(';');
  }

  function isAccessoryOf(item, buildId) {
    var p = (item && item.properties) || {};
    return String(p._build_id || '') === buildId &&
      String(p._pab_accessory_line || '') === 'true';
  }

  function isParentOf(item, buildId) {
    var p = (item && item.properties) || {};
    return String(p._build_id || '') === buildId &&
      String(p._custom_build || '') === 'true';
  }

  /* Canonical string for one build. Returns '' when the build is not present,
     which callers must treat as "cannot fingerprint", never as "unchanged". */
  function canonicalBuild(cart, buildId) {
    var id = String(buildId || '').trim();
    if (!id || !cart || !Array.isArray(cart.items)) return '';

    var items = cart.items;
    var parent = null;
    for (var i = 0; i < items.length; i++) {
      if (isParentOf(items[i], id)) { parent = items[i]; break; }
    }
    if (!parent) return '';

    var accessories = items
      .filter(function (item) { return isAccessoryOf(item, id); })
      .map(function (item) {
        return {
          variantId: String(item.variant_id || ''),
          quantity: parseInt(item.quantity, 10) || 0,
          properties: serializeProperties(item.properties)
        };
      })
      // Shopify does not guarantee cart ordering, so sort before serialising.
      .sort(function (a, b) {
        if (a.variantId !== b.variantId) return a.variantId < b.variantId ? -1 : 1;
        return a.quantity - b.quantity;
      })
      .map(function (a) {
        return a.variantId + ':' + a.quantity + ':' + a.properties;
      });

    return [
      SCHEMA,
      'parent:' + String(parent.variant_id || '') + ':' + (parseInt(parent.quantity, 10) || 0),
      'props:' + serializeProperties(parent.properties),
      'acc:' + accessories.join('|')
    ].join('|');
  }

  /* FNV-1a over the canonical string. Compact enough for sessionStorage while
     remaining a pure function of the canonical form. */
  function fold(text) {
    var hash = 0x811c9dc5;
    for (var i = 0; i < text.length; i++) {
      hash ^= text.charCodeAt(i);
      hash = (hash + (hash << 1) + (hash << 4) + (hash << 7) +
              (hash << 8) + (hash << 24)) >>> 0;
    }
    return SCHEMA + ':' + ('0000000' + hash.toString(16)).slice(-8) + ':' + text.length;
  }

  function fingerprintBuild(cart, buildId) {
    var canonical = canonicalBuild(cart, buildId);
    return canonical ? fold(canonical) : '';
  }

  window.PABBuildFingerprint = {
    SCHEMA: SCHEMA,
    /* Exposed so tests and the harness can assert the policy without
       re-declaring it. Returns a copy; callers cannot mutate the policy. */
    excluded: function () {
      var out = {};
      Object.keys(EXCLUDED).forEach(function (k) { out[k] = EXCLUDED[k]; });
      return out;
    },
    canonical: canonicalBuild,
    fingerprint: fingerprintBuild
  };
})();
